import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { buildEntityDocs, type EntityDoc } from '@/lib/entitySearch';

const TTL_MS = 60_000;

let cache: { key: string; at: number; docs: EntityDoc[] } | null = null;
let inFlight: { key: string; promise: Promise<EntityDoc[]> } | null = null;

function currentKey(): string {
  try {
    return localStorage.getItem('apiKey') ?? '';
  } catch {
    return '';
  }
}

// Drop any cached or in-flight index (call on logout).
export function clearEntityIndex(): void {
  cache = null;
  inFlight = null;
}

function ok<T>(r: PromiseSettledResult<T>): T | undefined {
  return r.status === 'fulfilled' ? r.value : undefined;
}

function loadEntityDocs(): Promise<EntityDoc[]> {
  const key = currentKey();
  if (cache && cache.key === key && Date.now() - cache.at < TTL_MS) return Promise.resolve(cache.docs);
  if (inFlight && inFlight.key === key) return inFlight.promise;
  const promise: Promise<EntityDoc[]> = Promise.allSettled([
    api.apps.listAll(),
    api.endpoints.listAll(),
    api.catalog.list(),
    api.externalUsers.list(),
    api.containers.list({ limit: 100 }), // first 100 only (server caps containers at 100 per page)
  ])
    .then((results) => {
      const [apps, endpoints, catalogs, users, containers] = results;
      const docs = buildEntityDocs({
        apps: ok(apps),
        endpoints: ok(endpoints),
        catalogs: ok(catalogs),
        users: ok(users),
        containers: ok(containers),
      });
      // Don't cache a total failure (next open retries) or a result for a key that is no longer current.
      if (results.some((r) => r.status === 'fulfilled') && currentKey() === key) {
        cache = { key, at: Date.now(), docs };
      }
      return docs;
    })
    .catch((): EntityDoc[] => [])
    .finally(() => {
      if (inFlight?.promise === promise) inFlight = null;
    });
  inFlight = { key, promise };
  return promise;
}

// Entity docs for the command palette. Loads lazily the first time `enabled` is true;
// undefined until the first load completes.
export function useEntityIndex(enabled: boolean): EntityDoc[] | undefined {
  const [docs, setDocs] = useState<EntityDoc[] | undefined>(undefined);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void loadEntityDocs().then((d) => {
      if (!cancelled) setDocs(d);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled]);
  return docs;
}
